import React, { useState, useEffect } from 'react';
import { Shield, Plus, Trash2, Loader2, LogOut, Users, FileText, Building2, ClipboardList, Edit2, Check, X, CreditCard, Power, ChevronDown, ChevronUp, Phone, Mail, Calendar, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const saFetch = (path, options = {}) => {
  const token = localStorage.getItem('sa_token');
  return fetch(`${API_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers }
  }).then(r => r.json());
};

function TenantCard({ tenant, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: tenant.name || '',
    contact_name: tenant.contact_name || '',
    contact_email: tenant.contact_email || '',
    contact_phone: tenant.contact_phone || '',
    billing_note: tenant.billing_note || '',
    last_paid_at: tenant.last_paid_at ? tenant.last_paid_at.split('T')[0] : '',
    next_due_at: tenant.next_due_at ? tenant.next_due_at.split('T')[0] : '',
  });

  const isOverdue = tenant.next_due_at && new Date(tenant.next_due_at) < new Date();
  const isDueSoon = tenant.next_due_at && !isOverdue && (new Date(tenant.next_due_at) - new Date()) < 7 * 24 * 60 * 60 * 1000;

  const handleSave = async () => {
    setSaving(true);
    const data = await saFetch(`/superadmin/tenants/${tenant.id}`, { method: 'PATCH', body: JSON.stringify(form) });
    setSaving(false);
    if (!data.error) { setEditing(false); onUpdate(); }
    else alert(data.error);
  };

  const handleToggle = async () => {
    if (!window.confirm(tenant.active ? `Avaktivera "${tenant.name}"? Användarna kan inte logga in.` : `Aktivera "${tenant.name}"?`)) return;
    const data = await saFetch(`/superadmin/tenants/${tenant.id}/toggle`, { method: 'POST' });
    if (!data.error) onUpdate();
    else alert(data.error);
  };

  const handleMarkPaid = async () => {
    const nextDue = window.prompt('Nästa förfallodatum (ÅÅÅÅ-MM-DD):', 
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    if (nextDue === null) return;
    const data = await saFetch(`/superadmin/tenants/${tenant.id}/mark-paid`, { method: 'POST', body: JSON.stringify({ next_due_at: nextDue || null }) });
    if (!data.error) onUpdate();
    else alert(data.error);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Ta bort "${tenant.name}" och ALL dess data permanent?`)) return;
    if (!window.confirm('Är du helt säker? Detta går inte att ångra.')) return;
    await saFetch(`/superadmin/tenants/${tenant.id}`, { method: 'DELETE' });
    onUpdate();
  };

  return (
    <div className={`bg-white rounded-2xl border overflow-hidden transition-all ${!tenant.active ? 'opacity-60 border-slate-200' : isOverdue ? 'border-red-200' : isDueSoon ? 'border-amber-200' : 'border-slate-200'}`}>
      {/* Header */}
      <div className="p-5 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-black text-lg">{tenant.name}</h3>
            {!tenant.active && (
              <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-500">Avaktiverad</span>
            )}
            {isOverdue && (
              <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full bg-red-100 text-red-600 flex items-center gap-1">
                <AlertCircle size={10} /> Förfallen
              </span>
            )}
            {isDueSoon && (
              <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full bg-amber-100 text-amber-700">Förfaller snart</span>
            )}
          </div>
          <p className="text-xs text-slate-400">{tenant.user_count || 0} användare · {tenant.quote_count || 0} offerter · Skapad {new Date(tenant.created_at).toLocaleDateString('sv-SE')}</p>
          {tenant.contact_name && <p className="text-sm text-slate-600 mt-1 font-medium">{tenant.contact_name}</p>}
          {tenant.contact_email && <p className="text-xs text-slate-400">{tenant.contact_email}{tenant.contact_phone ? ` · ${tenant.contact_phone}` : ''}</p>}
        </div>

        {/* Quick actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => { setEditing(!editing); setExpanded(true); }}
            className="p-2 rounded-xl text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Redigera">
            <Edit2 size={16} />
          </button>
          <button onClick={handleToggle}
            className={`p-2 rounded-xl transition-colors ${tenant.active ? 'text-slate-400 hover:text-amber-600 hover:bg-amber-50' : 'text-amber-500 hover:text-green-600 hover:bg-green-50'}`}
            title={tenant.active ? 'Avaktivera' : 'Aktivera'}>
            <Power size={16} />
          </button>
          <button onClick={() => setExpanded(!expanded)}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Betalningsstatus */}
      <div className="px-5 pb-4 flex items-center gap-3 flex-wrap">
        <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full font-bold ${
          !tenant.last_paid_at ? 'bg-slate-100 text-slate-500' :
          isOverdue ? 'bg-red-50 text-red-600' :
          isDueSoon ? 'bg-amber-50 text-amber-700' :
          'bg-green-50 text-green-700'
        }`}>
          <CreditCard size={12} />
          {tenant.last_paid_at 
            ? `Betalt ${new Date(tenant.last_paid_at).toLocaleDateString('sv-SE')}`
            : 'Ingen betalning registrerad'}
        </div>
        {tenant.next_due_at && (
          <div className="text-xs text-slate-400 flex items-center gap-1">
            <Calendar size={12} />
            Förfaller {new Date(tenant.next_due_at).toLocaleDateString('sv-SE')}
          </div>
        )}
        <button onClick={handleMarkPaid}
          className="text-xs text-blue-600 font-bold hover:underline ml-auto">
          + Registrera betalning
        </button>
      </div>

      {/* Expanderat innehåll */}
      {expanded && (
        <div className="border-t border-slate-100 p-5">
          {editing ? (
            <div className="space-y-4">
              {/* Skrivskyddad företagsprofil */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-3 flex items-center gap-1">
                  🔒 Uppgifter från kunden (skrivskyddade)
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600">
                  <div><span className="text-slate-400">Företag:</span> {tenant.cp_name || tenant.name}</div>
                  {tenant.cp_email && <div><span className="text-slate-400">E-post:</span> {tenant.cp_email}</div>}
                  {tenant.cp_phone && <div><span className="text-slate-400">Telefon:</span> {tenant.cp_phone}</div>}
                  {tenant.cp_org_nr && <div><span className="text-slate-400">Org.nr:</span> {tenant.cp_org_nr}</div>}
                  {tenant.cp_address && <div><span className="text-slate-400">Adress:</span> {[tenant.cp_address, tenant.cp_zip, tenant.cp_city].filter(Boolean).join(', ')}</div>}
                  {!tenant.cp_email && !tenant.cp_phone && <div className="col-span-2 text-slate-300 italic text-xs">Kunden har inte fyllt i profilen än.</div>}
                </div>
              </div>

              {/* Adminens egna fält */}
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Adminnoteringar</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { label: 'Intern kontaktperson', key: 'contact_name', placeholder: 'T.ex. ansvarig säljare' },
                  { label: 'Intern kontakt e-post', key: 'contact_email', type: 'email', placeholder: '' },
                  { label: 'Intern kontakt telefon', key: 'contact_phone', placeholder: '' },
                  { label: 'Senast betalt', key: 'last_paid_at', type: 'date' },
                  { label: 'Nästa förfallodag', key: 'next_due_at', type: 'date' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider block mb-1">{f.label}</label>
                    <input
                      type={f.type || 'text'}
                      value={form[f.key]}
                      placeholder={f.placeholder || ''}
                      onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                ))}
              </div>
              <div>
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider block mb-1">Fakturanotering</label>
                <textarea
                  value={form.billing_note}
                  onChange={e => setForm(p => ({ ...p, billing_note: e.target.value }))}
                  rows={2}
                  placeholder="Interna noteringar om betalning, avtal etc."
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)} className="flex items-center gap-1 px-4 py-2 border border-slate-200 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-50">
                  <X size={14} /> Avbryt
                </button>
                <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Spara
                </button>
                <button onClick={handleDelete} className="ml-auto flex items-center gap-1 px-4 py-2 border border-red-200 text-red-600 rounded-xl text-sm font-bold hover:bg-red-50">
                  <Trash2 size={14} /> Radera tenant
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Kontaktinfo */}
              {/* Profil från tenant */}
              <div className="mb-3">
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-2">Uppgifter från företagsprofil</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(tenant.cp_email || tenant.admin_email) && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Mail size={13} className="text-slate-400 flex-shrink-0" />
                      <span className="truncate">{tenant.cp_email || tenant.admin_email}</span>
                    </div>
                  )}
                  {tenant.cp_phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone size={13} className="text-slate-400 flex-shrink-0" />
                      {tenant.cp_phone}
                    </div>
                  )}
                  {tenant.cp_org_nr && (
                    <div className="text-sm text-slate-500">Org.nr: {tenant.cp_org_nr}</div>
                  )}
                  {(tenant.cp_address || tenant.cp_city) && (
                    <div className="text-sm text-slate-500">{[tenant.cp_address, tenant.cp_zip, tenant.cp_city].filter(Boolean).join(', ')}</div>
                  )}
                  {!tenant.cp_email && !tenant.cp_phone && !tenant.cp_org_nr && (
                    <p className="text-xs text-slate-300 italic col-span-2">Kunden har inte fyllt i sin företagsprofil än.</p>
                  )}
                </div>
              </div>
              {/* Adminnoteringar */}
              {(tenant.contact_name || tenant.billing_note) && (
                <div className="border-t border-slate-100 pt-3 mt-3">
                  <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-2">Adminnoteringar</p>
                  {tenant.contact_name && <p className="text-sm text-slate-600 mb-1">Kontakt: {tenant.contact_name}</p>}
                  {tenant.billing_note && (
                    <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-sm text-amber-800">
                      <span className="font-bold text-xs uppercase text-amber-600 block mb-1">Notering</span>
                      {tenant.billing_note}
                    </div>
                  )}
                </div>
              )}
              {!tenant.cp_email && !tenant.cp_phone && !tenant.contact_name && !tenant.billing_note && (
                <p className="text-sm text-slate-400 italic">Inga uppgifter ännu. Kunden fyller i sin profil efter inloggning.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SuperAdminDashboard({ email, onLogout }) {
  const [tab, setTab] = useState('tenants');
  const [tenants, setTenants] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all'); // all | active | inactive | overdue | unpaid
  const [appsLoading, setAppsLoading] = useState(true);
  const [form, setForm] = useState({ tenantName: '', adminEmail: '' });
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { loadTenants(); loadApplications(); }, []);

  const loadTenants = async () => {
    setLoading(true);
    const data = await saFetch('/superadmin/tenants');
    setTenants(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  const loadApplications = async () => {
    setAppsLoading(true);
    const data = await saFetch('/superadmin/applications');
    setApplications(Array.isArray(data) ? data : []);
    setAppsLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true); setError(''); setMessage('');
    const data = await saFetch('/superadmin/tenants', { method: 'POST', body: JSON.stringify(form) });
    if (data.error) setError(data.error);
    else { setMessage(data.message); setForm({ tenantName: '', adminEmail: '' }); loadTenants(); }
    setCreating(false);
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Godkänn ansökan och skapa konto? Välkomstmail skickas till sökanden.')) return;
    const data = await saFetch(`/superadmin/applications/${id}/approve`, { method: 'POST' });
    if (data.error) alert(data.error);
    else { alert(data.message); loadApplications(); loadTenants(); }
  };

  const handleDeclineApp = async (id) => {
    const reason = window.prompt('Anledning till avslag (valfritt):');
    if (reason === null) return;
    const data = await saFetch(`/superadmin/applications/${id}/decline`, { method: 'POST', body: JSON.stringify({ reason }) });
    if (data.error) alert(data.error);
    else loadApplications();
  };

  const pendingCount = applications.filter(a => a.status === 'pending').length;

  const filteredTenants = tenants.filter(t => {
    const q = tenantSearch.toLowerCase();
    const matchSearch = !q || 
      (t.cp_name || t.name || '').toLowerCase().includes(q) ||
      (t.cp_email || t.admin_email || '').toLowerCase().includes(q) ||
      (t.cp_org_nr || '').toLowerCase().includes(q) ||
      (t.cp_city || '').toLowerCase().includes(q);
    
    const now = new Date();
    const isOverdue = t.next_due_at && new Date(t.next_due_at) < now;
    const matchFilter =
      tenantFilter === 'all' ? true :
      tenantFilter === 'active' ? t.active :
      tenantFilter === 'inactive' ? !t.active :
      tenantFilter === 'overdue' ? isOverdue :
      tenantFilter === 'unpaid' ? !t.last_paid_at : true;

    return matchSearch && matchFilter;
  });
  const overdueCount = tenants.filter(t => t.next_due_at && new Date(t.next_due_at) < new Date()).length;

  const STATUS = {
    pending:  { label: 'Väntar', cls: 'bg-amber-100 text-amber-700' },
    approved: { label: 'Godkänd', cls: 'bg-green-100 text-green-700' },
    declined: { label: 'Avslagen', cls: 'bg-red-100 text-red-600' },
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-purple-600 rounded-xl flex items-center justify-center">
              <Shield size={18} className="text-white" />
            </div>
            <div>
              <h1 className="font-black text-lg leading-none">iOffer Superadmin</h1>
              <p className="text-xs text-slate-400">{email}</p>
            </div>
          </div>
          <button onClick={onLogout} className="flex items-center gap-2 text-slate-400 hover:text-red-500 text-sm font-bold transition-colors">
            <LogOut size={16} /> Logga ut
          </button>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex gap-0">
            <button className="flex items-center gap-2 px-5 py-3 text-sm font-black border-b-2 border-purple-600 text-purple-600">
              <Building2 size={16} />
              Kunder ({tenants.length})
              {overdueCount > 0 && <span className="bg-amber-500 text-white text-[10px] rounded-full px-1.5 py-0.5 font-black leading-none">{overdueCount}</span>}
              {pendingCount > 0 && <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5 font-black leading-none">{pendingCount} nya</span>}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">

        {/* KUNDER/TENANTS */}
        {tab === 'tenants' && (
          <div className="space-y-6">
            {/* Väntande ansökningar */}
            {applications.filter(a => a.status === 'pending').map(app => (
              <div key={app.id} className="bg-white rounded-2xl border-2 border-blue-200 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full bg-blue-100 text-blue-700">Ny ansökan</span>
                    </div>
                    <h3 className="font-black text-base">{app.company_name}</h3>
                    <p className="text-sm text-slate-600">{app.contact_name} · {app.email}{app.phone ? ` · ${app.phone}` : ''}</p>
                    {app.org_nr && <p className="text-xs text-slate-400">Org.nr: {app.org_nr}</p>}
                    {app.message && <p className="text-sm italic text-slate-500 mt-1 bg-slate-50 px-3 py-2 rounded-xl">"{app.message}"</p>}
                    <p className="text-xs text-slate-400 mt-1">{new Date(app.created_at).toLocaleString('sv-SE')}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => handleDeclineApp(app.id)}
                      className="px-3 py-2 border-2 border-red-200 text-red-600 rounded-xl text-sm font-bold hover:bg-red-50">Avslå</button>
                    <button onClick={() => handleApprove(app.id)}
                      className="px-3 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700">✓ Godkänn</button>
                  </div>
                </div>
              </div>
            ))}

            {/* Statistik */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: Building2, label: 'Kunder', value: tenants.length, color: 'text-purple-600 bg-purple-50' },
                { icon: Users, label: 'Användare', value: tenants.reduce((a, t) => a + (t.user_count || 0), 0), color: 'text-blue-600 bg-blue-50' },
                { icon: FileText, label: 'Offerter', value: tenants.reduce((a, t) => a + (t.quote_count || 0), 0), color: 'text-green-600 bg-green-50' },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s.color}`}>
                    <s.icon size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-black">{s.value}</p>
                    <p className="text-xs text-slate-400 font-bold uppercase">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Skapa manuellt */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-black text-base mb-4 flex items-center gap-2"><Plus size={18} className="text-purple-600" /> Skapa ny kund manuellt</h3>
              <form onSubmit={handleCreate} className="flex gap-3 flex-wrap">
                <input value={form.tenantName} onChange={e => setForm({ ...form, tenantName: e.target.value })} required
                  placeholder="Företagsnamn" className="flex-1 min-w-[180px] border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-400" />
                <input value={form.adminEmail} onChange={e => setForm({ ...form, adminEmail: e.target.value })} required type="email"
                  placeholder="Admin e-post" className="flex-1 min-w-[200px] border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-400" />
                <button type="submit" disabled={creating} className="bg-purple-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
                  {creating ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Skapa
                </button>
              </form>
              {message && <p className="text-green-600 text-sm font-bold mt-3 bg-green-50 px-4 py-2 rounded-xl">{message}</p>}
              {error && <p className="text-red-600 text-sm font-bold mt-3 bg-red-50 px-4 py-2 rounded-xl">{error}</p>}
            </div>

            {/* Sök och filter */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input
                  value={tenantSearch}
                  onChange={e => setTenantSearch(e.target.value)}
                  placeholder="Sök på namn, e-post, org.nr, stad..."
                  className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-400 bg-white"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {[
                  { key: 'all', label: 'Alla' },
                  { key: 'active', label: 'Aktiva' },
                  { key: 'inactive', label: 'Avaktiverade' },
                  { key: 'overdue', label: 'Förfallna' },
                  { key: 'unpaid', label: 'Ej betalat' },
                ].map(f => (
                  <button key={f.key} onClick={() => setTenantFilter(f.key)}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-colors ${tenantFilter === f.key ? 'bg-purple-600 text-white' : 'bg-white border border-slate-200 text-slate-500 hover:border-purple-300'}`}>
                    {f.label}
                    {f.key === 'all' && ` (${tenants.length})`}
                  </button>
                ))}
              </div>
            </div>

            {/* Kundlista */}
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin text-purple-600" size={32} /></div>
            ) : filteredTenants.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                <p className="text-slate-400 font-bold">Inga kunder matchar sökningen.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTenants.map(t => <TenantCard key={t.id} tenant={t} onUpdate={loadTenants} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
